import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api.js";
import { Today, TODAY } from "../customers/Today.js";
import { Companies } from "../customers/Companies.js";
import { GroupList } from "../customers/GroupList.js";
import { GroupPage, type GroupTab } from "../customers/GroupPage.js";
import { NewGroup } from "../customers/NewGroup.js";
import { Settings } from "../customers/Settings.js";
import { Flag, flagOf, liveGroups, useSetup } from "../customers/groups.js";
import { useNarrow } from "../ui/narrow.js";
import { SideHead, SideMenu, sideIcon, type SideItem } from "./chrome.js";

/**
 * **Customers** (23, [ADR 0037](../../../../docs/adr/0037-customers-is-one-loop-configured-from-the-business-page-for-businesses-and-for-the-public.md)):
 * finding this business its next customer.
 *
 * **Organised by group since 2026-11-04** ([38](../../../../docs/38-customers-by-group.md),
 * [ADR 0068](../../../../docs/adr/0068-customers-is-organised-by-group-and-joogoos-runs-each-group-inside-the-owner-s-limits.md)).
 * It was three screens — Today, Companies, Set up — and the group, the thing every company, search and plan already
 * belonged to, was an unnamed card inside Set up under one letter that looked like everybody's. Now the group is
 * what a person opens:
 *
 * - **Today** — every yes from every group, in the order a person owes them.
 * - **Groups** — all of them, for a business with dozens: folders, state, results and money per row.
 * - **All companies** — the one list, with a group filter.
 * - **Pinned** — the starred groups, or the busiest when none is starred, so one group is one click away.
 * - **New group** — three questions.
 * - **Settings**, at the foot — only what every group shares.
 *
 * A business with one group sees that group in the menu and opens it in one press, which is the whole of *keep one
 * group simple*.
 */

type Fixed = "today" | "groups" | "companies" | "new" | "settings";
type View = { screen: Fixed } | { screen: "group"; id: string; tab: GroupTab };
type Key = Fixed | `group:${string}`;

/** How many groups the menu pins when none is starred: the busiest few, never a second list. */
const PINNED_WHEN_NONE_STARRED = 3;

export function Customers({ onMailbox }: { onMailbox: () => void }) {
  const [view, setView] = useState<View>({ screen: "today" });
  const narrow = useNarrow();
  const setup = useSetup();
  // The one number worth putting on Today: somebody answered, and that is a person's job.
  const today = useQuery({ queryKey: TODAY, queryFn: () => api.today() });
  const waiting = today.data?.replies.length ?? 0;

  const live = liveGroups(setup.data);
  const starred = live.filter((g) => g.starred);
  const pinned = starred.length
    ? starred
    : [...live].sort((a, b) => (b.found + b.written) - (a.found + a.written)).slice(0, PINNED_WHEN_NONE_STARRED);
  // What waits for a person in one group: its letters to approve and its answers.
  const owed = (id: string) =>
    (today.data?.toApprove.filter((l) => l.groupId === id).length ?? 0) + (today.data?.replies.filter((r) => r.groupId === id).length ?? 0);

  const items: SideItem<Key>[] = [
    { key: "today", label: "Today", hint: "everything waiting for you", mark: sideIcon("chat"), count: waiting },
    { key: "groups", label: "Groups", hint: `${live.length} · who you write to`, mark: sideIcon("users") },
    { key: "companies", label: "All companies", hint: "every group, one list", mark: sideIcon("list") },
    ...pinned.map((g, i): SideItem<Key> => ({
      key: `group:${g.id}`, label: g.name, hint: g.paused ? "paused" : `${g.found} found · ${g.written} written`,
      mark: <Flag code={flagOf(g)} />, count: owed(g.id), dim: g.paused,
      ...(i === 0 ? { section: { title: "Pinned", note: starred.length ? "the ones you star" : "your busiest" } } : {}),
    })),
    { key: "new", label: "New group", hint: "three questions", mark: sideIcon("plus") },
    { key: "settings", label: "Settings", hint: "shared by every group", mark: sideIcon("sliders"), atFoot: true },
  ];

  const current: Key = view.screen === "group"
    ? (pinned.some((g) => g.id === view.id) ? `group:${view.id}` : "groups")
    : view.screen;
  const pick = (k: Key) => setView(k.startsWith("group:") ? { screen: "group", id: k.slice(6), tab: "overview" } : { screen: k as Fixed });
  const openGroup = (id: string, tab: GroupTab = "overview") => setView({ screen: "group", id, tab });

  return (
    <div className={narrow ? "h-full flex flex-col min-h-0" : "h-full flex min-h-0"}>
      <SideMenu label="Customers" current={current} onPick={pick} narrow={narrow}
        head={<SideHead icon="users" title="Customers" note="finding your next customer" />}
        items={items} />
      <main className="flex-1 min-w-0 min-h-0 overflow-y-auto">
        <div className="px-4 md:px-6 py-5 flex flex-col gap-4">
          {view.screen === "group"
            ? <GroupPage id={view.id} tab={view.tab} onTab={(tab) => openGroup(view.id, tab)} onGone={() => setView({ screen: "groups" })} />
            : view.screen === "today" ? <Today />
              : view.screen === "groups" ? <GroupList onOpen={openGroup} onNew={() => setView({ screen: "new" })} />
                : view.screen === "companies" ? <Companies />
                  : view.screen === "new" ? <NewGroup onMade={(id) => openGroup(id, "letters")} onCancel={() => setView({ screen: "groups" })} />
                    : <Settings onMailbox={onMailbox} />}
        </div>
      </main>
    </div>
  );
}

